import { faCircleCheck } from '@fortawesome/free-regular-svg-icons/faCircleCheck';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons/faMapMarkerAlt';
import { faSuitcase } from '@fortawesome/free-solid-svg-icons/faSuitcase';
import { faTruck } from '@fortawesome/free-solid-svg-icons/faTruck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CardMui5 from '@mui/material/Card';
import CardActionAreaMui5 from '@mui/material/CardActionArea';
import { alpha as alphaMui5, styled as styledMui5 } from '@mui/material/styles';
import { CSSProperties } from '@mui/material/styles/createMixins';
import { Theme as ThemeMui5 } from '@mui/material/styles/createTheme';
import TypographyMui5 from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import NextLinkMui5 from 'components/atoms/NextLink';
import {
  creditScoreInputAtom,
  downPaymentInputSelectorFamily,
  dueAtSigningTypeInputSelectorFamily,
  milesInputAtom,
  moInputSelector,
  scanModeInputAtom,
  searchDownPaymentInputAtom,
  upfrontAcquisitionFeeInputAtomFamily,
} from 'containers/deal/recoil/atoms';
import { useGTagTrackOfferCard } from 'hooks/useGTagTrackOfferCard';
import has from 'lodash/has';
import { FC, useEffect, useState } from 'react';
import {
  RecoilLoadable,
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { userAtom } from 'recoil/atoms';
import {
  auctionRebatesSelector,
  programSelector,
  userAuctionRebatesSelector,
} from 'recoil/selectors';
import { convertRebateToAppliedRebate } from 'shared/incentives/convertRebateToAppliedRebate';
import { getAuctionAppliedRebates } from 'shared/incentives/getAuctionAppliedRebates';
import { AuctionFromSearch } from 'shared/types/api/getAuctions';
import { GetProgramRequest } from 'shared/types/api/getProgram';
import { MSAPIAppliedRebate, MSAPIScanMode } from 'shared/types/marketScan';
import { Auction, AuctionStatus } from 'shared/types/models';
import {
  DEFAULT_CREDIT_SCORE,
  DEFAULT_MILES_PER_YEAR,
  DEFAULT_TERM_LENGTH,
  SearchTerm,
  TARIFF_FREE_VEHICLES,
} from 'shared/utils/constants';
import {
  evaluateProgramZipcode,
  getAuctionCurrentSellingPrice,
  getBuyItNowAuctionSellingPrice,
  getUserCreditScore,
  isBestDeal,
} from 'shared/utils/functions';
import { filterZipcodeAtom } from '../../../services/zipcode/recoil';
import { getUserLastBid } from '../../../shared/utils/getUserLastBid';
import { green } from '../../../theme/colors';
import { OfferCardFooter } from './OfferCardFooter';
import { OfferCardHeader } from './OfferCardHeader';
import { OfferCardMedia } from './OfferCardMedia';
import { OfferCardPrice } from './OfferCardPrice';
import { rebatesDialogAuctionAtom } from './recoil/atoms';

type Props = {
  auction: AuctionFromSearch;
  ignoreFeatured?: boolean;
  extendHeight?: boolean;
  ignoreProcessedImage: boolean;
  lightMode?: boolean;
};

type OfferCardRibbonsProps = {
  isBest: boolean;
  isFuture: boolean;
  isEnded: boolean;
  isFeatured: boolean;
  areAnyBids: boolean;
};

const OfferCardRibbon = styledMui5('div')<{
  color: CSSProperties['color'];
}>(
  ({ color }) => `
  top: 10px;
  right: -120px;
  width: 300px;
  text-align: center;
  font-weight: bold;
  font-size: 1.5rem !important;
  padding: 4px;
  text-decoration: none;
  position: absolute;
  text-shadow: 0 3px 10px rgba(0, 0, 0, 0.5);
  transform: rotate(45deg) translateY(100%);
  color: white;
  z-index: 100;
  font-size: 30px;
  background-color: ${color};`,
);

const OfferCardRibbonsClient: FC<OfferCardRibbonsProps> = ({
  isBest,
  ...props
}) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Skipp best deal ribbon for SSR
  if (!isClient) {
    return <OfferCardRibbons isBest={false} {...props} />;
  }

  return <OfferCardRibbons isBest={isBest} {...props} />;
};

const OfferCardRibbons: FC<OfferCardRibbonsProps> = ({
  isBest,
  isEnded,
  isFuture,
  isFeatured,
  areAnyBids,
}) => {
  if (isFeatured) {
    const greenColor = alphaMui5(green[600], 0.84);
    return <OfferCardRibbon color={greenColor}>Limited Offer</OfferCardRibbon>;
  }
  if (isBest && !isFuture) {
    return <OfferCardRibbon color={'#F22F2FD8'}>Best Deal</OfferCardRibbon>;
  }
  if (isEnded && areAnyBids) {
    return <OfferCardRibbon color={'#9E9E9ED8'}>Sold</OfferCardRibbon>;
  }
  if (isFuture) {
    return <OfferCardRibbon color={'#223559D8'}>Coming soon</OfferCardRibbon>;
  }
  return null;
};

const OfferCardContainer = styledMui5(CardMui5, {
  shouldForwardProp(propName) {
    return propName !== 'isShadowed' && propName !== 'isFeatured';
  },
})<{
  isShadowed: boolean;
  isFeatured?: boolean;
}>(
  ({ isShadowed, isFeatured }) => `
  &.MuiCard-root {
    position: relative;
    ${isShadowed ? 'pointer-events: none;' : ''}
    &.MuiPaper-root {
      ${isFeatured ? 'background-color: #fff8f0' : ''}
    }
    & .MuiCardActionArea-focusHighlight {
      display: none;
    }
  }
  &.MuiCard-root:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    transition: box-shadow 0.2s;
  }`,
);

const MediaHolder = styledMui5('div', { skipSx: false })(
  ({ theme }) => `
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid ${theme.palette.grey[200]};`,
);

const MediaChipsContainer = styledMui5('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'left',
  position: 'absolute',
  width: '100%',
  gap: theme.spacing(1),
  padding: theme.spacing(0, 2),
  transform: `translateY(calc(-100% - ${theme.spacing(1)}))`,
}));

const CardChip = styledMui5('div', { skipSx: false })(
  ({ theme }) => `
  display: flex;
  align-items: center;
  background-color: ${theme.palette.grey[100]}; 
  padding: ${theme.spacing(0.5)};
  border-radius: 4px;
  float: left;`,
);

const tariffFreeChipStyles: (
  theme?: ThemeMui5,
) => SystemStyleObject<ThemeMui5> = () => ({
  background: '#F8D85A',
  marginLeft: 'auto',
});

const OfferCardContent = styledMui5('div', {
  shouldForwardProp: (prop) => prop !== 'extendHeight',
})<{
  extendHeight?: boolean;
}>(
  ({ theme, extendHeight }) => `
  padding: ${theme.spacing(0, 2, extendHeight ? 4 : 2)};`,
);

type GetProgramRequestDefaultProps = {
  auction: Auction;
  rebates?: MSAPIAppliedRebate[];
  creditScore?: number;
  miles?: number;
  mo?: SearchTerm;
  zipcode?: string;
  isBuyItNow?: boolean;
};

const getProgramRequestDefault = ({
  auction,
  creditScore = DEFAULT_CREDIT_SCORE,
  miles = DEFAULT_MILES_PER_YEAR,
  mo = DEFAULT_TERM_LENGTH,
  zipcode,
  isBuyItNow = false,
  rebates,
}: GetProgramRequestDefaultProps): GetProgramRequest => {
  const salesPrice = isBuyItNow
    ? getBuyItNowAuctionSellingPrice(auction.msrp, auction.buy_it_now_discount)
    : getAuctionCurrentSellingPrice(auction);

  return {
    creditScore,
    demoMileage: auction.offer.mileage,
    downPayment: 0,
    isOwnersChoice: false,
    miles,
    mo,
    msd: 0,
    msrp: auction.msrp,
    invoicePrice: auction.invoice_price,
    onePayLease: false,
    upfrontAcquisitionFee: false,
    zeroDriveOff: false,
    monthlyPayment: null,
    salesPrice,
    zipcode: zipcode,
    dealerZipcode: auction.offer.zip[0],
    vehicleId: auction.offer.vehicle_id,
    waiveTheAcquisitionFee: false,
    rebates: rebates ?? [],
    auctionId: auction.id,
    scanMode: MSAPIScanMode.Lease,
  };
};

export const OfferCard: FC<Props> = ({
  auction,
  ignoreFeatured,
  extendHeight,
  ignoreProcessedImage,
  lightMode,
}: Props) => {
  const user = useRecoilValue(userAtom);
  const miles = useRecoilValue(milesInputAtom);
  const mo = useRecoilValue(moInputSelector);
  const userZipcode = user?.zipcode;
  const filterZipcode = useRecoilValue(filterZipcodeAtom);
  const requestZipcode = evaluateProgramZipcode({
    userZipcode,
    filterZipcode,
  });
  const creditScoreFilter = useRecoilValue(creditScoreInputAtom);
  const creditScore = getUserCreditScore(user, creditScoreFilter);
  const isFuture =
    new Date(auction.start_time).getTime() > new Date().getTime();

  const openRebateDialogForAuction = useSetRecoilState(
    rebatesDialogAuctionAtom,
  );

  const isFeatured = !ignoreFeatured && auction.is_featured;

  const isAuctionEnded =
    auction.status === AuctionStatus.Completed ||
    auction.status === AuctionStatus.Expired;

  const selectedRebates =
    useRecoilValueLoadable(
      userAuctionRebatesSelector(auction.id),
    ).valueMaybe() || [];

  const requestParams: GetProgramRequestDefaultProps = {
    mo,
    miles,
    creditScore,
    auction,
    rebates: selectedRebates.map(convertRebateToAppliedRebate),
    zipcode: requestZipcode,
    isBuyItNow: !Number.isFinite(auction.discount),
  };

  const request = getProgramRequestDefault(requestParams);

  const winnerProgram = RecoilLoadable.of(auction.bids?.[0]?.min_program);

  const dueAtSigningType = useRecoilValue(
    dueAtSigningTypeInputSelectorFamily(auction.id),
  );

  const userLastBid = getUserLastBid(auction.bids, user);

  const downPaymentAtomFamily = useRecoilValue(
    downPaymentInputSelectorFamily(auction.id),
  );
  const searchDownPaymentFinance = useRecoilValue(searchDownPaymentInputAtom);
  const scanMode = useRecoilValue(scanModeInputAtom);
  const isLease = scanMode === MSAPIScanMode.Lease;

  const upfrontAcquisitionFee = useRecoilValue(
    upfrontAcquisitionFeeInputAtomFamily(auction.id),
  );

  const dueAtSigningWithProgram = userLastBid?.min_program
    ? userLastBid?.min_program.DueAtSigningType
    : dueAtSigningType;

  const currentProgram = useRecoilValueLoadable(
    programSelector({
      ...request,
      dueAtSigningType: dueAtSigningWithProgram,
      downPayment: isLease ? downPaymentAtomFamily : searchDownPaymentFinance,
      upfrontAcquisitionFee,
    }),
  );

  const isAuctionCompleted = auction.status === AuctionStatus.Completed;

  const program = isAuctionCompleted ? winnerProgram : currentProgram;

  const isProgramValid =
    program.state === 'loading' ||
    (program.state !== 'hasError' && Boolean(program.valueMaybe()));

  const isBest = isBestDeal(
    Number(program.valueMaybe()?.Payment),
    auction.msrp,
    isLease,
  );

  const auctionRebates = useRecoilValueLoadable(
    auctionRebatesSelector({
      id: auction.id,
    }),
  ).valueMaybe();

  const appliedRebates = getAuctionAppliedRebates({
    auctionRebates,
    program: program.valueMaybe(),
  });

  const { gtagTrackOfferCardClick } = useGTagTrackOfferCard();

  const makeName = auction?.offer?.make?.name;
  const modelName = auction?.offer?.model?.name;
  const href = lightMode
    ? `/deals?make=${makeName}&model=${modelName}`
    : `/deals/${auction.id}`;

  const className = lightMode ? 'js-model-card-link' : 'js-offer-card-link';

  const isDealTariffFree = TARIFF_FREE_VEHICLES.some(
    ({ make, model }) => makeName === make && modelName.includes(model),
  );

  return (
    <OfferCardContainer isShadowed={false} isFeatured={isFeatured}>
      {/* // eslint-disable-next-line
          // @ts-ignore: onClick is not a valid prop for CardActionArea */}
      <CardActionAreaMui5
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
        component={NextLinkMui5}
        href={href}
        disableRipple
        className={className}
        onClick={() => {
          if (lightMode) {
            return;
          }
          gtagTrackOfferCardClick();
        }}
      >
        {!lightMode && (
          <OfferCardRibbonsClient
            isBest={isBest}
            isFuture={isFuture}
            isEnded={isAuctionEnded}
            isFeatured={isFeatured}
            areAnyBids={auction.bids.length > 0}
          />
        )}
        {!lightMode && (
          <OfferCardHeader
            sx={{ width: '100%' }}
            auction={auction}
            isAuctionEnded={isAuctionEnded}
            isFutureAuction={isFuture}
          />
        )}
        <MediaHolder sx={{ width: '100%' }}>
          <OfferCardMedia
            auction={auction}
            ignoreProcessedImage={ignoreProcessedImage}
          />
          <MediaChipsContainer>
            {has(auction, 'distance') && (
              <CardChip>
                <TypographyMui5
                  variant='caption'
                  display='block'
                  color='inherit'
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} />{' '}
                  {auction.distance === '50 mi' ? '< 50 mi' : auction.distance}
                </TypographyMui5>
              </CardChip>
            )}
            {auction?.free_delivery && (
              <CardChip>
                <TypographyMui5
                  display='block'
                  variant='caption'
                  color='inherit'
                >
                  <FontAwesomeIcon icon={faTruck} /> Free Delivery
                </TypographyMui5>
              </CardChip>
            )}
            {auction?.offer?.user?.dealership?.name && (
              <CardChip>
                <TypographyMui5
                  display='block'
                  variant='caption'
                  color='inherit'
                >
                  <FontAwesomeIcon icon={faSuitcase} />{' '}
                  {auction.offer.user.dealership.name}
                </TypographyMui5>
              </CardChip>
            )}
            {isDealTariffFree && (
              <CardChip sx={tariffFreeChipStyles}>
                <TypographyMui5 display='block' variant='body2' color='inherit'>
                  <FontAwesomeIcon icon={faCircleCheck} /> <b>Tariff-Free</b>{' '}
                  Deal
                </TypographyMui5>
              </CardChip>
            )}
          </MediaChipsContainer>
        </MediaHolder>
      </CardActionAreaMui5>
      <OfferCardContent extendHeight={extendHeight}>
        <OfferCardPrice
          isProgramValid={isProgramValid}
          auction={auction}
          program={program}
          appliedRebates={appliedRebates}
          lightMode={lightMode}
          onDiscountClick={() => {
            if (!isAuctionCompleted) {
              openRebateDialogForAuction(auction);
            }
          }}
        />
        {!lightMode && <OfferCardFooter auction={auction} />}
      </OfferCardContent>
    </OfferCardContainer>
  );
};

OfferCard.displayName = 'OfferCard';
